import { useEffect, useRef, useState } from 'react'
import { useStore } from '../../store'
import { useIsMobile } from '../../lib/viewport'
import { SearchTypeahead } from '../search/SearchTypeahead'
import { Icon } from '../ui'

/**
 * docs/design-handoff/tossker-redesign/README.md §1 "Header": `flex 1 1
 * 340px, max 460px`, 36px tall, `rounded-control` (8px), a 16px search icon
 * in `--text-subtle`. Task 19 wires the Hudu-style typeahead dropdown
 * against this same `q`/`searchOpen` state — the input and its open/closed
 * border are otherwise unchanged from Task 13.
 *
 * The README also puts a "⌘K hint chip" on the right of this field. Left
 * out here, same reasoning as the header's omitted bell (task-2 brief,
 * binding decision 3): there is no keyboard shortcut anywhere in this
 * console wired to open search, so a hint chip would advertise a capability
 * that does not exist.
 *
 * ON A PHONE IT IS AN ICON UNTIL YOU TAP IT.
 *
 * `min-w-[170px]` below is a floor this field cannot go under, and it was one
 * of the reasons the header could not fit a phone at all: the brand, this
 * field, the status chip and the right-hand group summed to roughly 637px of
 * IRREDUCIBLE width against a 390pt screen, so the whole document grew to
 * ~640px and every pane below it was dragged sideways with it. Andrew's
 * screenshots were the console scrolled halfway off his own phone.
 *
 * Collapsing to a 36px button is what buys the room back. Expanded, the field
 * takes the whole header rather than competing with the brand for it — there
 * is no width at which both fit, so the honest answer is to give it all of
 * one row for as long as somebody is typing.
 */
const PLACEHOLDER = 'Search comms, people, companies, tickets, words in a chat…'

/** Shorter on a phone: the desktop placeholder is a sentence, and a phone shows the first three words of it. */
const PLACEHOLDER_MOBILE = 'Search comms, people, tickets…'

export function SearchBox() {
  const q = useStore((s) => s.q)
  const searchOpen = useStore((s) => s.searchOpen)
  const set = useStore((s) => s.set)
  const isMobile = useIsMobile()
  const [expanded, setExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Focus follows the tap. An expanded field somebody still has to tap again
  // is two taps to do one thing.
  useEffect(() => {
    if (expanded) inputRef.current?.focus()
  }, [expanded])

  // Rotating a phone into landscape (or dragging a desktop window narrow and
  // back) must not strand the header in the other layout's state.
  useEffect(() => {
    if (!isMobile) setExpanded(false)
  }, [isMobile])

  function collapse() {
    setExpanded(false)
    set({ searchOpen: false })
  }

  const input = (
    <input
      ref={inputRef}
      type="text"
      value={q}
      onChange={(e) => set({ q: e.target.value })}
      onFocus={() => set({ searchOpen: true })}
      // NOT `onBlur` on mobile: the blur fires when a finger lands on a
      // result, which would close the dropdown before the tap ever reached
      // it. The explicit ✕ is how an expanded field closes there.
      onBlur={isMobile ? undefined : () => set({ searchOpen: false })}
      placeholder={isMobile ? PLACEHOLDER_MOBILE : PLACEHOLDER}
      aria-label="Search"
      className={[
        'h-9 w-full rounded-control border bg-card pl-9 pr-3 md:max-w-[460px]',
        'text-[13px] text-hi placeholder:text-lo focus:outline-none',
        searchOpen ? 'border-cc-blue' : 'border-subtle',
      ].join(' ')}
    />
  )

  if (isMobile && !expanded) {
    return (
      <button
        type="button"
        data-testid="search-open-btn"
        aria-label="Search"
        onClick={() => setExpanded(true)}
        className="ml-auto flex h-9 w-9 flex-none items-center justify-center rounded-control text-lo hover:text-hi"
      >
        <Icon name="search" size={18} />
      </button>
    )
  }

  if (isMobile) {
    // Over the header, not inside its flex row: sharing the row is the thing
    // that does not fit.
    return (
      <div data-testid="search-expanded" className="absolute inset-x-0 top-0 z-30 flex h-[60px] items-center gap-2 bg-card px-4">
        <div className="relative min-w-0 flex-1">
          <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lo" />
          {input}
          <SearchTypeahead />
        </div>
        <button
          type="button"
          data-testid="search-close-btn"
          aria-label="Close search"
          onClick={collapse}
          className="flex h-9 w-9 flex-none items-center justify-center rounded-control text-lo hover:text-hi"
        >
          <Icon name="x" size={18} />
        </button>
      </div>
    )
  }

  return (
    <div className="relative min-w-[170px] flex-[1_1_340px]">
      <Icon
        name="search"
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lo"
      />
      {input}
      <SearchTypeahead />
    </div>
  )
}
